import { useState } from "react";
import { PILLARS } from "@/lib/content";
import { ConceptStack } from "@/components/site/platform-options/ConceptStack";
import { ConceptDock } from "@/components/site/platform-options/ConceptDock";
import { ConceptBento } from "@/components/site/platform-options/ConceptBento";

const CONCEPTS = [
  {
    id: "stack",
    label: "Option 1 · Architecture Stack",
    comp: ConceptStack,
    note: "A layered execution stack (Semantic Core at the base building up to Business Applications), with the AI Forward Deployed Engineer as an accelerator spine and the whole thing wrapped in an AI TRiSM governance frame. Hover the accelerator to see it energise every layer.",
  },
  {
    id: "dock",
    label: "Option 2 · Interactive Dock",
    comp: ConceptDock,
    note: "A compact capability dock on the left; hover or click any item to spotlight it on the right stage. Only one capability's details show at a time, so it always fits one screen. FDE is styled as the dark accelerator; TRiSM sits at the base as the trust layer.",
  },
  {
    id: "bento",
    label: "Option 3 · Bento Horizon",
    comp: ConceptBento,
    note: "A dense, editorial bento grid: four pillars across the top, a full-width AI Forward Deployed Engineer highlight bar, and an AI TRiSM trust strip along the bottom. Everything visible at a glance, no scroll.",
  },
];

export default function PlatformOptions() {
  const core = PILLARS.slice(0, 4);
  const fde = PILLARS[4];
  const trism = PILLARS[5];
  const [active, setActive] = useState("dock");

  const activeConcept = CONCEPTS.find((c) => c.id === active);
  const Comp = activeConcept.comp;

  return (
    <div className="min-h-screen bg-[#211307] text-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-10">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#E8AA68]">Section 2 — Layout Options</p>
        <h1 className="font-display text-2xl lg:text-4xl font-medium mt-2 tracking-tight">Pick a layout for “The Platform”</h1>
        <p className="text-sm text-white/60 mt-3 max-w-2xl leading-relaxed">
          Each option below is engineered to fit within a single screen — no scrolling. Toggle between them, then tell me which
          one to ship (or mix and match).
        </p>

        <div className="flex flex-wrap gap-2 mt-6">
          {CONCEPTS.map((c) => (
            <button
              key={c.id}
              data-testid={`option-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`rounded-full px-4 py-2 text-[12px] font-semibold border transition-all duration-300 ${
                active === c.id
                  ? "bg-[#E8AA68] text-[#211307] border-[#E8AA68]"
                  : "bg-white/5 text-white/70 border-white/15 hover:border-white/40 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <p className="text-[12px] text-white/45 mt-4 max-w-3xl leading-relaxed" data-testid="option-note">
          {activeConcept.note}
        </p>
      </div>

      {/* Single-viewport frame */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-14 pt-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/35 ml-2">Single-screen preview</span>
        </div>
        <div
          className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.8)]"
          style={{ height: "82vh" }}
          data-testid="platform-viewport-frame"
        >
          <Comp core={core} fde={fde} trism={trism} />
        </div>
      </div>
    </div>
  );
}
